/**
 * Custom hook for opening OneDrive Picker via central auth domain popup
 * Handles cross-domain communication using postMessage API
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
    GooglePickerResult,
    GooglePickerMessage,
    isValidOrigin,
    getMainDomain,
    openCenteredPopup
} from "../google-picker/google-picker-message.util";

interface UseOneDrivePickerPopupOptions {
    /** Use SDK 8.0 picker instead of legacy */
    useSdk8?: boolean;
    /** Picker mode: "onedrive" for OneDrive, "sharepoint" for SharePoint sites */
    mode?: "onedrive" | "sharepoint";
    /** Callback when picker selection is successful */
    onSuccess?: (url: string, accessToken: string) => void;
    /** Callback when picker is cancelled */
    onCancel?: () => void;
    /** Callback on error */
    onError?: (error: string) => void;
}

interface UseOneDrivePickerPopupReturn {
    /** Opens the OneDrive Picker popup */
    openPicker: () => void;
    /** Whether the picker popup is currently open */
    isOpen: boolean;
    /** The last result from the picker */
    result: GooglePickerResult | null;
    /** Loading state while waiting for picker */
    isLoading: boolean;
}

/**
 * Gets the OneDrive Picker URL on the auth domain
 */
const getPickerUrl = (useSdk8: boolean, mode: "onedrive" | "sharepoint" = "onedrive"): string => {
    const hostname = window.location.hostname;

    // Handle localhost development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        const baseUrl = `http://localhost:${window.location.port || 3000}/onedrive-picker`;
        const params = new URLSearchParams({
            returnOrigin: window.location.origin
        });
        if (useSdk8) {
            params.set("sdk8", "true");
        }
        if (mode === "sharepoint") {
            params.set("mode", "sharepoint");
        }
        return `${baseUrl}?${params.toString()}`;
    }

    const pickerDomain = process.env.REACT_APP_LOGIN_DOMAIN;
    if (!pickerDomain) {
        throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
    }

    const params = new URLSearchParams({
        returnOrigin: window.location.origin
    });
    if (useSdk8) {
        params.set("sdk8", "true");
    }
    if (mode === "sharepoint") {
        params.set("mode", "sharepoint");
    }

    return `${pickerDomain}/onedrive-picker?${params.toString()}`;
};

/**
 * Hook for managing OneDrive Picker popup with cross-domain communication
 */
export const useOneDrivePickerPopup = (
    options: UseOneDrivePickerPopupOptions = {}
): UseOneDrivePickerPopupReturn => {
    const { useSdk8 = false, mode = "onedrive", onSuccess, onCancel, onError } = options;

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<GooglePickerResult | null>(null);
    const popupRef = useRef<Window | null>(null);
    const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
            }
        };
    }, []);

    // Handle incoming messages from the picker popup
    const handleMessage = useCallback(
        (event: MessageEvent) => {
            console.log("useOneDrivePickerPopup: Received message from origin:", event.origin);

            // Validate origin
            const mainDomain = getMainDomain();
            if (!isValidOrigin(event.origin, [mainDomain])) {
                console.log("useOneDrivePickerPopup: Origin validation failed, ignoring message");
                return;
            }

            const message = event.data as GooglePickerMessage;

            if (!message || !message.type) {
                console.log("useOneDrivePickerPopup: Invalid message format, ignoring");
                return;
            }

            console.log("useOneDrivePickerPopup: Valid message received, type:", message.type);

            switch (message.type) {
                case "GOOGLE_PICKER_READY":
                    console.log("useOneDrivePickerPopup: Picker is ready");
                    setIsLoading(false);
                    break;

                case "GOOGLE_PICKER_RESULT":
                    console.log("useOneDrivePickerPopup: Got picker result", message.payload);
                    if (message.payload) {
                        setResult(message.payload);

                        if (message.payload.success && message.payload.url) {
                            console.log(
                                "useOneDrivePickerPopup: Calling onSuccess with URL:",
                                message.payload.url
                            );
                            onSuccess?.(message.payload.url, message.payload.accessToken || "");
                        } else if (message.payload.error) {
                            console.log(
                                "useOneDrivePickerPopup: Calling onError:",
                                message.payload.error
                            );
                            onError?.(message.payload.error);
                        }
                    }
                    setIsOpen(false);
                    setIsLoading(false);
                    break;

                case "GOOGLE_PICKER_CANCEL":
                    console.log("useOneDrivePickerPopup: Picker was cancelled");
                    onCancel?.();
                    setIsOpen(false);
                    setIsLoading(false);
                    break;
            }
        },
        [onSuccess, onCancel, onError]
    );

    // Set up message listener only when popup is open
    useEffect(() => {
        if (!isOpen) return;
        console.log("useOneDrivePickerPopup: Setting up message listener");
        window.addEventListener("message", handleMessage);
        return () => {
            console.log("useOneDrivePickerPopup: Removing message listener");
            window.removeEventListener("message", handleMessage);
        };
    }, [handleMessage, isOpen]);

    // Monitor popup closure (user closing window manually)
    useEffect(() => {
        if (isOpen && popupRef.current) {
            checkIntervalRef.current = setInterval(() => {
                if (popupRef.current?.closed) {
                    setIsOpen(false);
                    setIsLoading(false);
                    if (checkIntervalRef.current) {
                        clearInterval(checkIntervalRef.current);
                        checkIntervalRef.current = null;
                    }
                }
            }, 500);
        }

        return () => {
            if (checkIntervalRef.current) {
                clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
            }
        };
    }, [isOpen]);

    const openPicker = useCallback(() => {
        try {
            const url = getPickerUrl(useSdk8, mode);
            console.log("useOneDrivePickerPopup: Opening picker popup:", url);

            const popupName =
                mode === "sharepoint" ? "SharePointPickerPopup" : "OneDrivePickerPopup";
            const popup = openCenteredPopup(url, popupName, 900, 650);

            if (popup) {
                popupRef.current = popup;
                setIsOpen(true);
                setIsLoading(true);
                setResult(null);
            } else {
                onError?.("Popup was blocked. Please allow popups for this site.");
            }
        } catch (error) {
            onError?.(error instanceof Error ? error.message : "Failed to open picker");
        }
    }, [useSdk8, mode, onError]);

    return {
        openPicker,
        isOpen,
        result,
        isLoading
    };
};

export default useOneDrivePickerPopup;
